<template>
  <div class="products">
    <!-- 当路径是 /products 时显示产品列表 -->
    <template v-if="$route.path === '/products'">
      <section class="about-content">
        <div class="container">
          <div class="about-header" ref="headerSection" :class="{ 'fade-in': isHeaderVisible }">
            <div class="title-box">
              <h2>产品服务</h2>
              <div class="title-line"></div>
              <p>以服务为载体，创建科技价值</p>
            </div>
          </div>

          <div class="solutions-grid" ref="solutionsSection" :class="{ 'fade-in': isSolutionsVisible }">
            <router-link 
              v-for="item in productItems" 
              :key="item.path"
              :to="item.path"
              class="solution-card"
            >
              <div class="card-inner">
                <div class="icon-wrapper">
                  <el-icon><component :is="item.icon" /></el-icon>
                </div>
                <div class="content">
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.description }}</p>
                  <span class="learn-more">
                    了解更多 <el-icon><ArrowRight /></el-icon>
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </section>
    </template>

    <!-- 路由出口，用于显示子页面 -->
    <router-view v-else></router-view>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { 
  ArrowRight,
  Monitor,
  SetUp,
  Operation,
  Box,
  DataLine,
  Connection,
  Platform,
  Lock,
  Service,
  Management
} from '@element-plus/icons-vue'
import { navigationData } from '@/data/navigation'

const headerSection = ref(null)
const solutionsSection = ref(null)
const isHeaderVisible = ref(false)
const isSolutionsVisible = ref(false)

// 添加图标映射
const iconMap = {
  'system': Monitor,
  'operation': Operation,
  'hardware': Box,
  'innovation': Platform,
  'data': DataLine,
  'custom': SetUp,
  'enterprise': Connection
}

// 修改 productItems 的处理方式
const productItems = (navigationData.find(item => item.key === 'products')?.children || []).map(item => ({
  ...item,
  icon: iconMap[item.key] || Monitor // 如果没有对应的图标，使用 Monitor 作为默认图标
}))

const handleScroll = () => {
  if (headerSection.value) {
    const rect = headerSection.value.getBoundingClientRect()
    isHeaderVisible.value = rect.top <= window.innerHeight * 0.8
  }
  if (solutionsSection.value) {
    const rect = solutionsSection.value.getBoundingClientRect()
    isSolutionsVisible.value = rect.top <= window.innerHeight * 0.8
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  handleScroll()
})
</script>

<style lang="scss" scoped>
.products {
  .about-content {
    padding: 80px 0;
    background: linear-gradient(135deg, #f8f9fc 0%, #fff 100%);

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }
  }

  .about-header {
    text-align: center;
    margin-bottom: 60px;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);

    &.fade-in {
      opacity: 1;
      transform: translateY(0);
    }

    .title-box {
      h2 {
        font-size: 36px;
        color: #333;
        margin-bottom: 15px;
        font-weight: 600;
        letter-spacing: 1px;
      }

      .title-line {
        width: 40px;
        height: 3px;
        background: #2b5dff;
        margin: 0 auto 20px;
      }

      p {
        font-size: 16px;
        color: #666;
        max-width: 600px;
        margin: 0 auto;
        line-height: 1.6;
      }
    }
  }

  .solutions-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);

    &.fade-in {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .solution-card {
    background: #fff;
    border-radius: 16px;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    overflow: hidden;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 30px rgba(43, 93, 255, 0.15);

      .learn-more {
        color: #2b5dff;
        gap: 12px;
      }

      .icon-wrapper {
        background: #2b5dff;
        .el-icon {
          color: #fff;
        }
      }
    }

    .card-inner {
      padding: 40px;
      display: flex;
      align-items: flex-start;
      gap: 25px;
    }

    .icon-wrapper {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
      background: rgba(43, 93, 255, 0.08);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      .el-icon {
        font-size: 26px;
        color: #2b5dff;
        transition: all 0.3s ease;
      }
    }

    .content {
      flex: 1;

      h3 {
        font-size: 20px;
        color: #333;
        margin-bottom: 12px;
        font-weight: 600;
      }

      p {
        color: #666;
        font-size: 14px;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .learn-more {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #666;
        font-size: 14px;
        transition: all 0.3s ease;

        .el-icon {
          font-size: 16px;
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .solutions-grid {
      gap: 25px;
    }

    .solution-card .card-inner {
      padding: 30px;
    }
  }

  @media (max-width: 992px) {
    .solutions-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  @media (max-width: 768px) {
    .about-content {
      padding: 60px 0;
    }

    .solution-card {
      .card-inner {
        padding: 25px;
        gap: 20px;
      }

      .icon-wrapper {
        width: 50px;
        height: 50px;

        .el-icon {
          font-size: 22px;
        }
      }

      .content {
        h3 {
          font-size: 18px;
          margin-bottom: 10px;
        }

        p {
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 15px;
        }
      }
    }
  }
}

.page-header {
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%);
    animation: pulse 4s ease-in-out infinite;
    opacity: 0.1;
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}
</style>